import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import { toast } from "sonner";

export default function InvoiceStatusDialog({ invoice, open, onClose, onSaved }) {
  const [payment, setPayment] = useState("unpaid");
  const [sent, setSent] = useState("not_sent");
  const [paidAt, setPaidAt] = useState("");
  const [records, setRecords] = useState([]);
  const [crid, setCrid] = useState("");
  const [number, setNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const isHistorical = !!invoice?.historical;

  useEffect(() => {
    if (!invoice) return;
    setPayment(invoice.payment_status || "unpaid");
    setSent(invoice.sent_status || "not_sent");
    setPaidAt(invoice.paid_at ? invoice.paid_at.slice(0, 10) : "");
    setCrid(invoice.client_record_id || "");
    setNumber(invoice.number || "");
    setAmount(invoice.amount != null ? String(invoice.amount) : "");
    setDescription(invoice.description || "");
    if (invoice.historical) {
      api.get("/admin/client-records").then(r => setRecords(r.data.records || [])).catch(() => {});
    }
  }, [invoice]);

  if (!open || !invoice) return null;

  const recordLabel = (r) => r.company_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.billing_email || "Fiche sans nom";

  const save = async () => {
    setBusy(true);
    try {
      let latest = invoice;
      // 1) historical metadata edits (only if changed)
      if (isHistorical) {
        const meta = {};
        if (crid !== invoice.client_record_id) meta.client_record_id = crid;
        if (number.trim() !== invoice.number) meta.number = number.trim();
        if (Number(amount) !== Number(invoice.amount)) meta.amount = Number(amount);
        if (description !== (invoice.description || "")) meta.description = description;
        if (Object.keys(meta).length > 0) {
          const { data } = await api.patch(`/admin/invoices/${invoice.id}/historical`, meta);
          latest = data;
        }
      }
      // 2) status edits
      const body = { payment_status: payment, sent_status: sent };
      if (payment === "paid" && paidAt) body.paid_at = paidAt;
      if (payment === "unpaid") body.paid_at = null;
      const { data } = await api.patch(`/admin/invoices/${invoice.id}/status`, body);
      latest = data;
      toast.success("Facture mise à jour");
      onSaved?.(latest);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4" data-testid="invoice-status-modal">
      <div className="cs-card p-6 max-w-md w-full my-8 relative bg-[#FAF8F5]">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-500 hover:text-black"><X size={18} /></button>
        <div className="cs-eyebrow text-[#0E7C90]">Facture {invoice.number}</div>
        <h3 className="cs-h3 mt-1 text-xl">{isHistorical ? "Modifier la facture historique" : "Modifier les statuts"}</h3>

        {isHistorical && (
          <div className="mt-5 space-y-3 border-b border-black/10 pb-5">
            <div className="cs-eyebrow text-neutral-500">Informations de la facture (historique)</div>
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Fiche client</span>
              <select className="cs-input mt-1 w-full" value={crid} onChange={e => setCrid(e.target.value)} data-testid="hist-record">
                <option value="">— Choisir une fiche —</option>
                {records.map(r => <option key={r.id} value={r.id}>{recordLabel(r)} · {r.billing_email}</option>)}
              </select>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Numéro de facture</span>
                <input className="cs-input mt-1 w-full" value={number} onChange={e => setNumber(e.target.value)} data-testid="hist-number" />
              </label>
              <label className="block">
                <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Montant TTC (€)</span>
                <input type="number" step="0.01" className="cs-input mt-1 w-full" value={amount} onChange={e => setAmount(e.target.value)} data-testid="hist-amount" />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Intitulé / objet</span>
              <input className="cs-input mt-1 w-full" value={description} onChange={e => setDescription(e.target.value)} data-testid="hist-description" />
            </label>
            <p className="text-[11px] text-neutral-500">Le PDF importé n'est pas modifié. Seules ces informations d'affichage sont corrigées.</p>
          </div>
        )}

        <div className="mt-6 space-y-4">
          <div>
            <span className="cs-eyebrow text-neutral-500">Statut de paiement</span>
            <div className="mt-1 flex gap-2">
              {[{ v: "unpaid", l: "À régler" }, { v: "paid", l: "Payée" }].map(o => (
                <button key={o.v} type="button" onClick={() => setPayment(o.v)}
                  className={`flex-1 px-3 py-2 border text-sm ${payment === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
                  data-testid={`status-pay-${o.v}`}>{o.l}</button>
              ))}
            </div>
            {invoice.payment_manual_updated_at && (
              <div className="mt-1 text-[11px] font-mono text-neutral-500">Dernière modif. manuelle : {fmtDateTime(invoice.payment_manual_updated_at)}</div>
            )}
            {payment === "paid" && (
              <label className="mt-2 block">
                <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">Date de paiement</span>
                <input type="date" className="cs-input mt-1 w-full sm:w-56" value={paidAt} onChange={e => setPaidAt(e.target.value)} data-testid="status-paid-at" />
                <span className="mt-1 block text-[11px] text-neutral-500">
                  Renseignée automatiquement lors d'un paiement Stripe. Modifiable ici pour les autres moyens de paiement.
                </span>
              </label>
            )}
          </div>

          <div>
            <span className="cs-eyebrow text-neutral-500">Statut d'envoi</span>
            <div className="mt-1 flex gap-2">
              {[{ v: "not_sent", l: "Non envoyée" }, { v: "sent", l: "Envoyée" }].map(o => (
                <button key={o.v} type="button" onClick={() => setSent(o.v)}
                  className={`flex-1 px-3 py-2 border text-sm ${sent === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
                  data-testid={`status-sent-${o.v}`}>{o.l}</button>
              ))}
            </div>
            {invoice.sent_at && (
              <div className="mt-1 text-[11px] font-mono text-neutral-500">Dernier envoi : {fmtDateTime(invoice.sent_at)}</div>
            )}
            {invoice.sent_manual_updated_at && (
              <div className="text-[11px] font-mono text-neutral-500">Dernière modif. manuelle : {fmtDateTime(invoice.sent_manual_updated_at)}</div>
            )}
          </div>

          <p className="text-[12px] text-neutral-500 leading-relaxed">
            Ces statuts peuvent aussi évoluer automatiquement (envoi via le site, paiement Stripe). Toute correction manuelle est enregistrée avec sa date.
          </p>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="cs-btn-ghost !text-black !border-black">Annuler</button>
          <button onClick={save} disabled={busy} className="cs-btn-primary disabled:opacity-40 inline-flex items-center gap-2" data-testid="status-save">
            {busy && <Loader2 size={14} className="animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
